import React, { useState, useRef, useEffect, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ScrollContext } from './App';
import {Mail, Phone, MapPin, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import emailjs from '@emailjs/browser';

const ContactIcon = ({ icon: Icon, text, link }) => {
  return (
    <motion.a
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      whileHover={{ scale: 1.03 }}
      className="flex items-center gap-4 p-4 rounded-xl group transition-colors duration-300 hover:bg-white/5"
    >
      <div className="p-3 bg-neutral-900 rounded-lg border border-neutral-800 group-hover:border-neutral-700 transition-all duration-300">
        <Icon className="w-6 h-6 text-neutral-400 group-hover:text-white transition-colors duration-300" />
      </div>
      <div>
        <p className="text-neutral-300 group-hover:text-white transition-colors duration-300">{text}</p>
      </div>
    </motion.a>
  );
};

const Notification = ({ isVisible, onClose, status, message }) => {
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -50 }}
          className={`fixed bottom-8 left-1/2 -translate-x-1/2 bg-neutral-900 border ${
            status === 'success' ? 'border-neutral-700' : 'border-red-500/30'
          } rounded-xl p-6 shadow-2xl ${
            status === 'success' ? 'shadow-white/5' : 'shadow-red-500/20'
          } z-50 min-w-[300px]`}
        >
          <div className="flex items-center gap-4">
            <div className={`rounded-full ${
              status === 'success' ? 'bg-white/10' : 'bg-red-500/20'
            } p-2`}>
              {status === 'success' ? (
                <CheckCircle className="w-6 h-6 text-white" />
              ) : (
                <AlertCircle className="w-6 h-6 text-red-500" />
              )}
            </div>
            <div>
              <h3 className="font-semibold text-white mb-1">
                {status === 'success' ? 'Message Sent!' : 'Error'}
              </h3>
              <p className="text-neutral-400 text-sm">{message}</p>
            </div>
          </div>
          <motion.button
            onClick={onClose}
            className="absolute top-2 right-2 text-neutral-500 hover:text-white"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            ×
          </motion.button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const ModernButton = ({ children, isLoading, ...props }) => {
  return (
    <motion.button
      {...props}
      disabled={isLoading}
      className="relative w-full py-4 px-6 rounded-xl bg-white text-black border border-neutral-700 font-bold overflow-hidden group transition-all duration-300 hover:bg-neutral-200 disabled:opacity-50 disabled:cursor-not-allowed"
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      <div className="relative z-10 flex items-center justify-center gap-2">
        {isLoading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Sending...</span>
          </>
        ) : (
          children
        )}
      </div>
    </motion.button>
  );
};

const Contact = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    status: 'success',
    message: ''
  });
  const formRef = useRef();
  const { setScrollContainer } = useContext(ScrollContext);
  const scrollRef = useRef(null);

  useEffect(() => {
    setScrollContainer(scrollRef.current);
    return () => setScrollContainer(null);
  }, [setScrollContainer]);

  useEffect(() => {
    // It's recommended to call init once in your application's entry point
    // For example, in index.js
    // emailjs.init("YOUR_PUBLIC_KEY"); 
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    // IMPORTANT: Replace with your actual EmailJS Service ID, Template ID, and Public Key
    try {
      const result = await emailjs.sendForm(
        'service_zdldvbr',   // Replace with your EmailJS Service ID
        'template_k4u9l0g',  // Replace with your EmailJS Template ID
        formRef.current,
        "FTkwRmFM8qWbDZHR3"    // Replace with your EmailJS Public Key
      );

      if (result.status === 200) {
        setNotification({
          show: true,
          status: 'success',
          message: "We'll get back to you soon!"
        });
        formRef.current.reset();
      } else {
        throw new Error(`Failed to send message. Status: ${result.status}`);
      }
    } catch (error) {
      setNotification({
        show: true,
        status: 'error',
        message: 'Failed to send message. Please try again.'
      });
    } finally {
      setIsLoading(false);
      setTimeout(() => {
        setNotification(prev => ({ ...prev, show: false }));
      }, 5000);
    }
  };

  return (
    <div ref={scrollRef} className="h-screen overflow-y-auto overflow-x-hidden custom-scrollbar flex flex-col">
      <div className="pt-32 pb-16 flex-grow">
        <div className="w-full">
          <Notification 
            isVisible={notification.show}
            status={notification.status}
            message={notification.message}
            onClose={() => setNotification(prev => ({ ...prev, show: false }))}
          />

          <div className="max-w-7xl mx-auto px-4">
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.7 }}
            className="text-center mb-4"
            >
            <h1 className="text-3xl md:text-4xl font-extrabold mb-2 text-white">
                Enquire with us
              </h1>
            <p className="text-base text-neutral-400 max-w-3xl mx-auto">
                Have an enquiry or question? Drop us a message, and we’ll get back to you promptly.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
              <motion.div 
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="lg:col-span-2 space-y-1"
              >
                <ContactIcon
                  icon={Phone}
                  text="+91 9243109916"
                  link="tel:+919243109916"
                />
                <ContactIcon
                  icon={Mail}
                  text="info@chavigroup.com"
                  link="mailto:info@chavigroup.com"
                />
                <ContactIcon
                  icon={MapPin}
                  text="G2-401, Shriram Shreyas Apartments, Bengaluru, Karnataka, India"
                  link="https://www.google.com/maps/place/Shriram+Shreyas+Apartments/@13.0650399,77.5678385,17.33z/data=!4m14!1m7!3m6!1s0x3bae1805b1799f27:0xb1b34179b2edadfd!2sShriram+Shreyas+Apartments!8m2!3d13.0651327!4d77.5695806!16s%2Fg%2F11b7ld4qpf!3m5!1s0x3bae1805b1799f27:0xb1b34179b2edadfd!8m2!3d13.0651327!4d77.5695806!16s%2Fg%2F11b7ld4qpf?entry=ttu&g_ep=EgoyMDI1MDkxNy4wIKXMDSoASAFQAw%3D%3D"
                />
              </motion.div>

              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.7, delay: 0.4 }}
              className="lg:col-span-3 bg-neutral-900/50 border border-neutral-800 rounded-2xl p-4 sm:p-6"
              >
              <form ref={formRef} className="space-y-3" onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-neutral-300 text-sm font-medium mb-2" htmlFor="from_name">
                        Name
                      </label>
                      <input
                        type="text"
                        id="from_name"
                        name="from_name"
                      className="w-full p-2 rounded-lg bg-neutral-900 text-neutral-200 border border-neutral-700 focus:outline-none focus:ring-2 focus:ring-neutral-600 transition-all duration-300"
                        placeholder="Your Name" 
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-neutral-300 text-sm font-medium mb-2" htmlFor="company_name">
                        Company name
                      </label>
                      <input
                        type="text"
                        id="company_name"
                        name="company_name"
                      className="w-full p-2 rounded-lg bg-neutral-900 text-neutral-200 border border-neutral-700 focus:outline-none focus:ring-2 focus:ring-neutral-600 transition-all duration-300"
                        placeholder="Your Company's Name" 
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-neutral-300 text-sm font-medium mb-2" htmlFor="email">
                        Email
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                      className="w-full p-2 rounded-lg bg-neutral-900 text-neutral-200 border border-neutral-700 focus:outline-none focus:ring-2 focus:ring-neutral-600 transition-all duration-300"
                        placeholder="Your Email Address" 
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-neutral-300 text-sm font-medium mb-2" htmlFor="phone_number">
                        Phone number
                      </label>
                      <input
                        type="tel"
                        id="phone_number"
                        name="phone_number"
                      className="w-full p-2 rounded-lg bg-neutral-900 text-neutral-200 border border-neutral-700 focus:outline-none focus:ring-2 focus:ring-neutral-600 transition-all duration-300"
                        placeholder="Your Phone Number" 
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-neutral-300 text-sm font-medium mb-2" htmlFor="country">
                      Country
                    </label>
                    <input
                      type="text"
                      id="country"
                      name="country"
                    className="w-full p-2 rounded-lg bg-neutral-900 text-neutral-200 border border-neutral-700 focus:outline-none focus:ring-2 focus:ring-neutral-600 transition-all duration-300"
                      placeholder="Your Country" 
                    />
                  </div>
                  <div>
                    <label className="block text-neutral-300 text-sm font-medium mb-2" htmlFor="subject">
                      Subject
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                    className="w-full p-2 rounded-lg bg-neutral-900 text-neutral-200 border border-neutral-700 focus:outline-none focus:ring-2 focus:ring-neutral-600 transition-all duration-300"
                      placeholder="Subject of your message" 
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-neutral-300 text-sm font-medium mb-2" htmlFor="message">
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                    rows="4"
                    className="w-full p-2 rounded-lg bg-neutral-900 text-neutral-200 border border-neutral-700 focus:outline-none focus:ring-2 focus:ring-neutral-600 transition-all duration-300 resize-none"
                      placeholder="How can we help you?"
                      required
                    />
                  </div>
                  <ModernButton type="submit" isLoading={isLoading}>
                    Send Message
                  </ModernButton>
                </form>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
      <footer className="w-full py-4">
        <p className="text-center text-sm text-neutral-600 dark:text-neutral-500">
            © 2025 Chavi Group . All rights reserved.
        </p>
      </footer>
    </div>
  );
}

export default Contact;